/**
 * CoOccurrenceIndex
 *
 * O(1) lookup helper built from `FacetCoOccurrence`. Given the currently
 * active filters, answers the question: "Would picking `value` on `field`
 * still leave at least one document?"
 *
 * The underlying co-occurrence map is symmetric (every ordered field-pair
 * is stored canonically with `field_a < field_b`). We flatten that into a
 * directed adjacency structure keyed by `"fieldA|valueA|fieldB"` so we can
 * look up "which fieldB-values are compatible with this specific fieldA
 * value" in constant time.
 *
 * Disable rule for a candidate `(field, value)`:
 *   For every OTHER field that already has at least one selected value,
 *   the candidate must be compatible with AT LEAST ONE of the selected
 *   values on that field (within-field selection is OR, cross-field is
 *   AND). If any other field has selections that are all incompatible,
 *   the candidate would yield zero results and is disabled.
 *
 * Important: a candidate is NEVER disabled because of its own field's
 * selection. Within a field we OR, so selecting an extra value can only
 * widen the result set on that axis.
 *
 * Alias values
 * ------------
 * Some facet values are synthetic aliases that no document carries
 * (`zenon_version=latest`, which the backend resolves to the newest real
 * version at query time). The co-occurrence map is built by aggregating over
 * documents, so it can never contain a pair mentioning an alias. Looking one
 * up would miss and — because the field-pair itself *is* tracked — fall
 * through to "proven incompatible", disabling `latest` as soon as anything
 * is selected on `source`, `source_type` or `language`.
 *
 * Both sides of every lookup are therefore mapped through `alias_for` first,
 * so an alias inherits the compatibility of the value it stands for.
 *
 * If the index is empty (no co-occurrence data) every candidate is
 * trivially allowed.
 */

import type { FacetCoOccurrence, FacetField } from '../types/search';

export interface CoOccurrenceIndex {
  /** True when no co-occurrence data is loaded \u2014 disables nothing. */
  readonly isEmpty: boolean;
  /** True when the field-pair is represented in the map. */
  hasFieldPair(fieldA: string, fieldB: string): boolean;
  /** True when `(fieldA=valueA, fieldB=valueB)` was observed together. */
  isCompatible(fieldA: string, valueA: string, fieldB: string, valueB: string): boolean;
}

const EMPTY_INDEX: CoOccurrenceIndex = {
  isEmpty: true,
  hasFieldPair: () => false,
  isCompatible: () => true,
};

/**
 * Build `"field|aliasValue" -> targetValue` from the facet fields.
 *
 * Chains are not followed: an alias whose target is itself an alias is not
 * something the backend produces, and resolving one step keeps this total.
 */
function buildAliasMap(fields: readonly FacetField[]): Map<string, string> {
  const aliases = new Map<string, string>();
  for (const field of fields) {
    for (const value of field.values ?? []) {
      if (value.alias_for) {
        aliases.set(`${field.field_name}|${value.value}`, value.alias_for);
      }
    }
  }
  return aliases;
}

/**
 * Build an O(1) lookup index from a co-occurrence map.
 *
 * Returned object treats the map as symmetric, so callers can ask
 * `isCompatible(a, va, b, vb)` regardless of which field was canonical.
 *
 * `fields` supplies the alias definitions (`FacetValue.alias_for`).
 */
export function buildCoOccurrenceIndex(
  data: FacetCoOccurrence | null | undefined,
  fields: readonly FacetField[],
): CoOccurrenceIndex {
  if (!data || !data.pairs || data.pairs.length === 0) {
    return EMPTY_INDEX;
  }

  const aliases = buildAliasMap(fields);
  const resolve = (field: string, value: string): string =>
    aliases.get(`${field}|${value}`) ?? value;

  // Pair-key -> Set of "valueA|valueB" tuples, stored in BOTH directions
  // so symmetric lookups are constant-time.
  const compat = new Map<string, Set<string>>();
  const knownPairs = new Set<string>();

  for (const block of data.pairs) {
    const { field_a, field_b, pairs } = block;
    if (!field_a || !field_b || !pairs || pairs.length === 0) continue;

    knownPairs.add(`${field_a}|${field_b}`);
    knownPairs.add(`${field_b}|${field_a}`);

    const forwardKey = `${field_a}|${field_b}`;
    const backwardKey = `${field_b}|${field_a}`;
    let forward = compat.get(forwardKey);
    if (!forward) {
      forward = new Set<string>();
      compat.set(forwardKey, forward);
    }
    let backward = compat.get(backwardKey);
    if (!backward) {
      backward = new Set<string>();
      compat.set(backwardKey, backward);
    }
    for (const [va, vb] of pairs) {
      forward.add(`${va}|${vb}`);
      backward.add(`${vb}|${va}`);
    }
  }

  return {
    isEmpty: false,
    hasFieldPair(fieldA: string, fieldB: string): boolean {
      return knownPairs.has(`${fieldA}|${fieldB}`);
    },
    isCompatible(fieldA, valueA, fieldB, valueB): boolean {
      // Same field is always compatible with itself (within-field is OR).
      if (fieldA === fieldB) return true;
      const bucket = compat.get(`${fieldA}|${fieldB}`);
      // If we have no entry for this field-pair at all, we cannot prove
      // incompatibility \u2014 allow it (conservative / fail-open).
      if (!bucket) return true;
      return bucket.has(`${resolve(fieldA, valueA)}|${resolve(fieldB, valueB)}`);
    },
  };
}

/**
 * Decide whether `(field, value)` should be disabled in the UI given the
 * currently selected filters.
 *
 * `selected` maps field name to selected values. Fields with no selected
 * values are ignored. The candidate's own field is also ignored.
 */
export function isFacetValueDisabled(
  index: CoOccurrenceIndex,
  field: string,
  value: string,
  selected: Record<string, readonly string[] | undefined>,
): boolean {
  if (index.isEmpty) return false;

  for (const [otherField, otherValues] of Object.entries(selected)) {
    if (!otherValues || otherValues.length === 0) continue;
    if (otherField === field) continue;
    // We need at least ONE compatible value on this other field.
    let anyCompatible = false;
    for (const otherValue of otherValues) {
      if (index.isCompatible(field, value, otherField, otherValue)) {
        anyCompatible = true;
        break;
      }
    }
    if (!anyCompatible) {
      // But only if we actually have data for this field-pair \u2014 otherwise
      // we'd over-disable. `isCompatible` returns true for unknown pairs,
      // so reaching here implies the pair IS tracked and no value matched.
      return true;
    }
  }
  return false;
}
